import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { faCaretDown, faCaretUp, faSort, faXmark } from '@fortawesome/free-solid-svg-icons';
import { Color, LegendPosition, ScaleType } from '@swimlane/ngx-charts';
import { DataAnalysisApiService } from '../../services/api/data-analysis-api.service';
import { UtilService } from '../../../../core/services/util/util.service';
import { PlatformService } from '../../../../core/services/platform/platform.service';

interface ChartDataset {
  name: string;
  series: Series[]
}
interface Series {
  name: string;
  value: number;
}

interface TableDataset {
  set_sku: string;
  name: string;
  stock: number;
  channels: ChannelSummary[];
  products: ProductSet[];
}
interface ChannelSummary {
  channel: string;
  total_amount: number;
  total_qty: number;
}
interface ProductSet {
  cost: number;
  name: string;
  price: number;
  qty: number;
  sku: string;
  srp_price: number;
}

@Component({
  selector: 'app-data-analysis-product-set',
  templateUrl: './data-analysis-product-set.component.html',
  styleUrl: './data-analysis-product-set.component.css'
})
export class DataAnalysisProductSetComponent implements OnInit {

  @Input() branchMode = false;

  faCaretDown = faCaretDown;
  faCaretUp = faCaretUp;
  faSort = faSort;
  faXmark = faXmark
  legendPosition = LegendPosition.Right

  tableDatasets: TableDataset[] = []

  channels: any[] = [];
  channelFiltered: any[] = [];

  // products: Array<{ sku: string; name: string }> = [];
  keywordProduct: string = '';
  productsFilter: Array<{ sku: string; name: string }> = [];
  showProductFilter = false;
  productSelected: Array<{ sku: string; name: string }> = [];

  productSet: Array<{ set_sku: string; name: string, in_stock: number; products: ProductSet[]; active: boolean; price_change: boolean; total_price: number; }> = [];
  keywordSet: string = '';
  setFilter: Array<{ set_sku: string; name: string }> = [];
  showSetFilter = false;
  setSelected: Array<{ set_sku: string; name: string, in_stock: number; products: ProductSet[]; active: boolean; price_change: boolean; total_price: number; }> = [];

  setSummary: Array<{ channel: string; set_sku: string; total_qty: number; total_amount: number }> = []
  setFiltered: Array<{ set_sku: string; total_qty: number; total_amount: number }> = []

  countDataSource = 0;
  pageSize = 10;
  pageIndex = 0;
  limit = 10;
  offset = 0;

  displayData: string = 'total_qty';
  channelSelected: string[] = [];
  readonly ALL_CHANNEL_MARKER = '__all__';

  selectedDate: { type: string, value: string } = { type: 'daypass', value: 'month' };

  form: FormGroup;
  loading: boolean = false;

  selectedRow: string = '';
  period: number = 30;
  performanceRows: Array<{ sku: string; channel: string; date: string; total_amount: number; total_qty: number; }> = []
  performanceDatasets: ChartDataset[] = []
  chartLoaded: boolean = false;

  sortSelected: string = '';
  sortBy: string = 'max';

  private fallbackColors = new Map<string, string>();
  performanceChartScheme: Color = { name: 'channel-line', selectable: true, group: ScaleType.Ordinal, domain: [] };


  constructor(
    private dataAnalysisApiService: DataAnalysisApiService,
    private utilService: UtilService,
    private platformService: PlatformService,
    private fb: FormBuilder,
  ) {
    this.form = this.fb.group({
      date: this.computeDateRange(this.selectedDate),
      sku: [''],
      channel: ['']
    });

  }

  handleOptionSelected(selectedDate: { type: string, value: string }) {
    this.selectedDate = selectedDate;
    this.form.get('date')?.setValue(this.computeDateRange(selectedDate));
  }

  private computeDateRange(option: { type: string, value: string }): string {
    const dayjs = this.utilService.dayjs;
    if (option.type === 'date-range') {
      const [s, e] = option.value.split(',');
      return `${dayjs(s).format('YYYY-MM-DD')},${dayjs(e).format('YYYY-MM-DD')}`;
    }
    if (option.value === '0') {
      const d = dayjs().format('YYYY-MM-DD');
      return `${d},${d}`;
    }
    if (option.value === '1') {
      const d = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
      return `${d},${d}`;
    }
    if (option.value === 'month') {
      const start = dayjs().startOf('month').format('YYYY-MM-DD');
      const end = dayjs().format('YYYY-MM-DD');
      return `${start},${end}`;
    }
    const start = dayjs().subtract(parseInt(option.value), 'day').format('YYYY-MM-DD');
    const end = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
    return `${start},${end}`;
  }


  ngOnInit(): void {
    const all = this.platformService?.channels || [];
    this.channels = all.filter(c => !!c.is_branch === this.branchMode);
    this.channelFiltered = this.channels;
    this.getDataReport();
  }

  async getDataReport() {
    if (this.loading) return;
    this.loading = true;
    const value = this.form.getRawValue()

    let filter: any = { limit: this.limit, offset: this.offset, sort_field: this.displayData, sort_by: this.sortBy }
    if (value.date) {
      filter['date'] = value.date
    }
    if (this.channelSelected.length > 0) {
      filter['field_channel'] = this.channelSelected.join()
      this.channelFiltered = this.channels.filter(c => this.channelSelected.includes(c.shop_ref))
    } else {
      filter['field_channel'] = this.channels.map((item) => item.shop_ref).toString()
      this.channelFiltered = this.channels
    }
    if (this.productSelected.length > 0) {
      filter['sku'] = this.productSelected.map(e => e.sku).join()
    }
    if (this.setSelected.length > 0) {
      filter['set_sku'] = this.setSelected.map(e => e.set_sku).join()
    }
    if (this.sortSelected) {
      filter['channel'] = this.sortSelected
    }

    this.setSummary = [];
    this.setFiltered = [];
    this.countDataSource = 0;
    this.selectedRow = ''

    try {
      const res: any = await this.dataAnalysisApiService.getProductSetSummaryReport(filter);
      if (res.success) {
        this.setFiltered = res.data.sets;
        this.setSummary = res.data.summary;
        this.countDataSource = res.data.count;
        const uniqueSetSku = [...new Set(this.setFiltered.map(e => e.set_sku))];
        if (uniqueSetSku.length > 0) {
          await this.findAllProductSet({ set_sku: uniqueSetSku.join(), fields: 'set_sku,name' });
        }
      }
      this.setFormattingData()
    } catch {
      this.setFiltered = [];
      this.setSummary = [];
      this.tableDatasets = [];
      this.countDataSource = 0;
    } finally {
      this.loading = false;
    }
  }

  async getAllSummaryReport(set_sku: string) {
    this.chartLoaded = false;
    let filter: any = {
      set_sku,
      date: `${this.utilService.dayjs().subtract(this.period, 'day').format('YYYY-MM-DD')},${this.utilService.dayjs().format('YYYY-MM-DD')}`
    }
    if (this.channelSelected.length > 0) {
      filter['channel'] = this.channelSelected.join()
    } else {
      filter['channel'] = this.channels.map((item) => item.shop_ref).toString()
    }
    try {
      const res: any = await this.dataAnalysisApiService.getProductSetDailyReport(filter);
      if (res.success) {
        this.performanceRows = res.data
        this.setFormattingPerformanceChart()
      } else {
        this.performanceRows = []
        this.performanceDatasets = []
      }
    } catch {
      this.performanceRows = []
      this.performanceDatasets = []
    } finally {
      this.chartLoaded = true;
    }
  }

  async searchProduct(keyword: string) {
    return await this.dataAnalysisApiService.searchProduct({ keyword, limit: 20 }).then((res: any) => {
      if (res.success) {
        return res.data;
      }
      return []
    })
  }

  async searchProductSet(keyword: string) {
    return await this.dataAnalysisApiService.searchProductSet({ keyword, limit: 20, active: true }).then((res: any) => {
      if (res.success) {
        return res.data;
      }
      return []
    })
  }

  async findAllProductSet(filter: any) {
    const allSku: string[] = String(filter.set_sku || '').split(',').filter(s => s);
    const pSku = allSku.filter(s => s.startsWith('P'));
    const tSku = allSku.filter(s => s.startsWith('T'));

    const requests: Array<Promise<any>> = [];
    if (pSku.length > 0) {
      requests.push(this.dataAnalysisApiService.getAllProductSet({ ...filter, set_sku: pSku.join() }));
    }
    if (tSku.length > 0) {
      requests.push(this.dataAnalysisApiService.getAllProductSetTikTok({ ...filter, set_sku: tSku.join() }));
    }

    try {
      const results = await Promise.all(requests);
      this.productSet = results
        .filter((res: any) => res?.success)
        .flatMap((res: any) => res.data || []);
    } catch {
      this.productSet = [];
    }
  }

  readonly TOTAL_ROW_NAME = 'รวม';

  setFormattingData() {
    const setRows: TableDataset[] = this.setFiltered.map(set => {
      const channels: ChannelSummary[] = this.channelFiltered.map(channel => {
        const res = this.setSummary.find(e => e.channel === channel.shop_ref && e.set_sku === set.set_sku);
        return {
          channel: channel.shop_ref,
          total_amount: res?.total_amount || 0,
          total_qty: res?.total_qty || 0
        };
      });
      channels.push({
        channel: '',
        total_amount: channels.reduce((n, { total_amount }) => n + total_amount, 0),
        total_qty: channels.reduce((n, { total_qty }) => n + total_qty, 0)
      });
      const detail = this.productSet.find(p => p.set_sku === set.set_sku);
      return {
        set_sku: set.set_sku,
        name: detail?.name || '',
        stock: detail?.in_stock || 0,
        products: detail?.products || [],
        channels
      };
    });

    this.tableDatasets = setRows.length > 0
      ? [...setRows, this.buildTotalRow(setRows)]
      : [];
  }

  private rebuildPerformanceChartScheme(): void {
    this.performanceChartScheme = {
      name: 'channel-line',
      selectable: true,
      group: ScaleType.Ordinal,
      domain: this.channels.map(c => c.color || this.channelFallbackColor(c.shop_ref))
    };
  }

  private channelFallbackColor(key: string): string {
    if (!this.fallbackColors.has(key)) {
      let hash = 0;
      for (let i = 0; i < key.length; i++) {
        hash = ((hash << 5) - hash) + key.charCodeAt(i);
        hash |= 0;
      }
      const hue = Math.abs(hash) % 360;
      this.fallbackColors.set(key, `hsl(${hue}, 65%, 55%)`);
    }
    return this.fallbackColors.get(key)!;
  }

  private buildTotalRow(rows: TableDataset[]): TableDataset {
    const channelCount = rows[0]?.channels.length || 0;
    const channels: ChannelSummary[] = Array.from({ length: channelCount }, (_, i) => ({
      channel: rows[0].channels[i].channel,
      total_amount: rows.reduce((s, r) => s + (r.channels[i]?.total_amount || 0), 0),
      total_qty: rows.reduce((s, r) => s + (r.channels[i]?.total_qty || 0), 0)
    }));
    return {
      set_sku: '',
      name: this.TOTAL_ROW_NAME,
      stock: rows.reduce((s, r) => s + r.stock, 0),
      products: [],
      channels
    };
  }

  setFormattingPerformanceChart() {
    this.rebuildPerformanceChartScheme();
    this.performanceDatasets = []
    for (const channel of this.channels) {

      const series = [];
      const performanceObj: any = this.performanceRows.filter((e: any) => e.channel === channel.shop_ref).reduce((a, v) => ({ ...a, [v.date]: v }), {})
      for (let index = this.period; index >= 0; index--) {
        const date = this.utilService.dayjs().subtract(index, 'day').format('YYYY-MM-DD');
        series.push({ name: date, value: this.displayData === 'total_qty' ? (performanceObj[date]?.total_qty || 0) : (performanceObj[date]?.total_amount || 0) })
      }

      this.performanceDatasets.push({
        name: channel.platform_name,
        series: series
      })

    }
  }

  get displayedChannelSelection(): string[] {
    if (this.channels.length > 0 && this.channelSelected.length === this.channels.length) {
      return [this.ALL_CHANNEL_MARKER, ...this.channelSelected];
    }
    return this.channelSelected;
  }

  onChannelSelectionChange(values: any[]) {
    const list = Array.isArray(values) ? values : [];
    const wasAllSelected = this.channels.length > 0
      && this.channelSelected.length === this.channels.length;
    const newHasMarker = list.includes(this.ALL_CHANNEL_MARKER);

    if (newHasMarker !== wasAllSelected) {
      this.channelSelected = newHasMarker
        ? this.channels.map(c => c.shop_ref)
        : [];
    } else {
      this.channelSelected = list.filter((v: unknown): v is string =>
        typeof v === 'string' && v !== this.ALL_CHANNEL_MARKER
      );
    }
  }

  removeChannelSelected(channel: any) {
    this.channelSelected = this.channelSelected.filter((e: string) => e !== channel.shop_ref)
  }

  displaychannel() {
    return this.channels.filter(c => this.channelSelected.includes(c.shop_ref))
  }

  displayDataOnchange(event: any) {
    this.displayData = event.value
    this.offset = 0;
    this.pageIndex = 0;
    this.getDataReport();
  }

async productSearch(dataSearch: any) {
    if (this.keywordProduct.length > 2) {
      try {
        const uniquesku = this.productSelected.map(e => e.sku);
        const results = await this.searchProduct(this.keywordProduct);
        this.productsFilter = (results || []).filter((e: any) => !uniquesku.includes(e.sku))
        this.showProductFilter = true;
      } catch {
        this.productsFilter = [];
        this.showProductFilter = false;
      }
    } else {
      this.showProductFilter = false;
    }
  }

  selectProduct(product: any) {
    this.productSelected.push(product)
    this.productsFilter = this.productsFilter.filter((e: any) => e.sku !== product.sku)
    if (this.productsFilter.length === 0) {
      this.showProductFilter = false;
    }
  }

  removeProductSelected(product: any) {
    this.productSelected = this.productSelected.filter(e => e.sku !== product.sku)
  }

  async productSetSearch(dataSearch: any) {
    if (this.keywordSet.length > 1) {
      try {
        const uniquesku = this.setSelected.map(e => e.set_sku);
        const results = await this.searchProductSet(this.keywordSet);
        this.setFilter = (results || []).filter((e: any) => !uniquesku.includes(e.set_sku))
        this.showSetFilter = true;
      } catch {
        this.setFilter = [];
        this.showSetFilter = false;
      }
    } else {
      this.showSetFilter = false;
    }
  }

  selectProductSet(set: any) {
    this.setSelected.push(set)
    this.setFilter = this.setFilter.filter((e: any) => e.set_sku !== set.set_sku)
    if (this.setFilter.length === 0) {
      this.showSetFilter = false;
    }
  }

  removeSetSelected(set: any) {
    this.setSelected = this.setSelected.filter(e => e.set_sku !== set.set_sku)
  }

  callBackPageChange = (data: any): void => {
    if (this.offset !== data.offset || this.limit !== data.limit) {
      this.offset = data.offset;
      this.limit = data.limit;
      this.getDataReport()
    }

    if (data.pageIndex !== undefined) {
      this.pageIndex = data.pageIndex;
    }
    if (data.limit !== undefined) {
      this.pageSize = data.limit;
    }
  }

  submitForm() {
    this.offset = 0;
    this.pageIndex = 0;
    this.sortSelected = '';
    this.sortBy = 'max'
    this.getDataReport();
  }

  selectedRowOnChange(row: any) {
    if (this.selectedRow === row.set_sku) {
      this.selectedRow = ''
    } else {
      this.selectedRow = row.set_sku
      this.getAllSummaryReport(this.selectedRow)
    }
  }

  sortData(shop_ref: string) {

    if (this.sortSelected !== shop_ref) {
      this.sortSelected = shop_ref;
      this.sortBy = 'max'
    } else {
      if (!this.sortBy) this.sortBy = 'max'
      else if (this.sortBy === 'max') this.sortBy = 'min'
      else if (this.sortBy === 'min') this.sortBy = 'max'
    }

    this.offset = 0;
    this.pageIndex = 0;
    this.getDataReport();
  }

  clearSearchProduct() {
    this.keywordProduct = ''
    this.showProductFilter = false
  }

  clearSearchSet() {
    this.keywordSet = ''
    this.showSetFilter = false
  }

  removeChannelSelectedAll() {
    this.channelSelected = []
  }

  removeProductSelectedAll() {
    this.productSelected = []
  }

  removeSetSelectedAll() {
    this.setSelected = []
  }



}
